import { Add as AddIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { concat, isEmpty, max, toNumber } from 'lodash';
import { useState } from 'react';
import SortableList, { SortableItem } from 'react-easy-sort';
import { Drawer } from 'src/components/drawer';
import { useModal } from 'src/hooks';
import {
  type IGcfProductListProduct,
  type IProduct,
  GcfGroupProductType,
} from 'src/interfaces';
import {
  extractProductsFromGCFGroup,
  lang,
  mapGcfGroupProducts,
  mapProductsToGCFProductArray,
  moveArrayItem,
  useUserRole,
} from 'src/utils';

import { ProductListModal } from '../../modal/productListModal';
import { EditProductPanel } from './editProductPanel/EditProductPanel';
import { LineupWidgetProps } from './LineupWidget.interface';
import {
  BackupContainer,
  HeaderTitle,
  LineupContainer,
  LineupSidebarContainer,
  LineupSidebarWrapper,
  SortableItemContainer,
} from './LineupWidget.styled';
import { ProductLine } from './productLine';

export const LineupWidget = (props: LineupWidgetProps) => {
  const productListModal = useModal();
  const { isOwnerOperator } = useUserRole();
  const [editProduct, setEditProduct] = useState<IGcfProductListProduct>();

  const [seed, setSeed] = useState<number>(0);

  const { products, backups } = extractProductsFromGCFGroup(props.group);

  const handleGroupChange =
    (
      field: GcfGroupProductType,
      operation: 'add' | 'delete' | 'update',
      order?: number,
    ) =>
    (value?: IProduct[] | IGcfProductListProduct) => {
      const targetProducts =
        field === GcfGroupProductType.PRODUCTS ? products : backups;

      let updatedProducts: IGcfProductListProduct[] = [];

      const maxProductOrder =
        toNumber(max(targetProducts.map((item) => item.order))) || 0;

      if (operation === 'add') {
        updatedProducts = concat(
          targetProducts,
          mapProductsToGCFProductArray(
            value as IProduct[],
            maxProductOrder + 1,
          ),
        );
      } else if (operation === 'delete') {
        updatedProducts = targetProducts.filter(
          (product) => product.order !== order,
        );
      } else if (operation === 'update') {
        const product = {
          ...(value as IGcfProductListProduct),
          config: {
            ...(value as IGcfProductListProduct).config,
            isBackupProduct: field === GcfGroupProductType.BACKUPS,
          },
        };
        setEditProduct(product);

        updatedProducts = targetProducts.map((product) => {
          if (product.order === (value as IGcfProductListProduct).order) {
            return value as IGcfProductListProduct;
          }

          return product;
        });
      }

      const newGroup = mapGcfGroupProducts(
        props.groupNumber,
        props?.group,
        field === GcfGroupProductType.PRODUCTS ? updatedProducts : products,
        field === GcfGroupProductType.BACKUPS ? updatedProducts : backups,
      );

      let hasProducts =
        Object.keys(newGroup?.products)?.length > 0 ||
        Object.keys(newGroup?.backups)?.length > 0;

      if (hasProducts) {
        props.handleGroupChange('update', newGroup);
      } else {
        props.handleGroupChange('delete', newGroup);
      }
    };

  const onEditProduct = (product?: IGcfProductListProduct) => () => {
    setEditProduct(product);
  };

  const handleSortProducts =
    (field: GcfGroupProductType) => (oldIndex: number, newIndex: number) => {
      const newProducts = moveArrayItem(
        field === GcfGroupProductType.PRODUCTS ? products : backups,
        oldIndex,
        newIndex,
      );

      const newGroup = mapGcfGroupProducts(
        props.groupNumber,
        props.group,
        field === GcfGroupProductType.PRODUCTS ? newProducts : products,
        field === GcfGroupProductType.BACKUPS ? newProducts : backups,
        false,
      );

      props.handleGroupChange('update', newGroup);
    };

  const openProductModal = (field: GcfGroupProductType) => () => {
    productListModal.onOpenModal(handleGroupChange(field, 'add'));
  };

  return (
    <LineupSidebarContainer>
      <LineupSidebarWrapper>
        <HeaderTitle>{lang.products}</HeaderTitle>
        <LineupContainer>
          <SortableList
            data-testid="sortable-list"
            onSortEnd={handleSortProducts(GcfGroupProductType.PRODUCTS)}
            draggedItemClassName="dragged">
            {products.map((product) => (
              <SortableItem key={product.order}>
                <SortableItemContainer>
                  <ProductLine
                    gcfProduct={product}
                    hideDeleteButton={props.hideDeleteButton}
                    isDisabled={false}
                    onDelete={handleGroupChange(
                      GcfGroupProductType.PRODUCTS,
                      'delete',
                      product.order,
                    )}
                    onEdit={onEditProduct(product)}
                    seed={seed}
                    setSeed={setSeed}
                  />
                </SortableItemContainer>
              </SortableItem>
            ))}
          </SortableList>
        </LineupContainer>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={openProductModal(GcfGroupProductType.PRODUCTS)}>
          {lang.addProduct}
        </Button>

        {props.showComponents.showProductBackups && (
          <BackupContainer>
            {(isOwnerOperator ? backups.length > 0 : true) && (
              <HeaderTitle>{lang.backup}</HeaderTitle>
            )}

            <LineupContainer>
              <SortableList
                onSortEnd={handleSortProducts(GcfGroupProductType.BACKUPS)}
                draggedItemClassName="dragged">
                {backups.map((product) => (
                  <SortableItem key={product.order}>
                    <SortableItemContainer>
                      <ProductLine
                        isDisabled={!!isOwnerOperator}
                        gcfProduct={product}
                        onDelete={handleGroupChange(
                          GcfGroupProductType.BACKUPS,
                          'delete',
                          product.order,
                        )}
                        onEdit={onEditProduct(product)}
                        seed={seed}
                        setSeed={setSeed}
                      />
                    </SortableItemContainer>
                  </SortableItem>
                ))}
              </SortableList>
            </LineupContainer>

            {!isOwnerOperator && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={openProductModal(GcfGroupProductType.BACKUPS)}>
                {lang.addBackup}
              </Button>
            )}
          </BackupContainer>
        )}
      </LineupSidebarWrapper>

      {productListModal.open && (
        <ProductListModal
          open={productListModal.open}
          onClose={productListModal.onCloseModal}
          onSelect={productListModal.customFn}
          heading={lang.addProducts}
          hasExactSearch={true}
          filters={
            !isEmpty(props.productCategoryIDs)
              ? [
                  `product_category_id=${props.productCategoryIDs
                    ?.sort()
                    ?.join('|')}`,
                ]
              : []
          }
        />
      )}
      {editProduct && (
        <Drawer open={!!editProduct}>
          <EditProductPanel
            showComponents={props.showComponents}
            componentType={props.componentType}
            editProduct={editProduct}
            productCategoryIDs={props.productCategoryIDs ?? []}
            themeId={props.themeId}
            onBackClick={onEditProduct(undefined)}
            onChange={handleGroupChange(
              editProduct.config.isBackupProduct
                ? GcfGroupProductType.BACKUPS
                : GcfGroupProductType.PRODUCTS,
              'update',
            )}
            componentConfig={props.componentConfig}
          />
        </Drawer>
      )}
    </LineupSidebarContainer>
  );
};
